import { ErrorNotification } from "@components/ErrorNotification"
import { Button } from "@components/ui/button"
import { Field } from "@components/ui/form/Field"
import { Link as UiLink } from "@components/ui/link"
import { parseError } from "@lib/parse-error"
import { Form, Formik } from "formik"
import { signIn, SignInResponse } from "next-auth/react"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { FC, useState } from "react"

type FormValues = {
  email: string
  password: string
}

export const LoginFormForm: FC = () => {
  const [error, setError] = useState<undefined | string>(undefined)
  const [errorCode, setErrorCode] = useState<undefined | string>(undefined)
  const [userEmail, setUserEmail] = useState<string>("")
  const router = useRouter()

  const isUserNotActivated = errorCode === "user-error:user-not-activated"

  if (isUserNotActivated) {
    return (
      <>
        {error && <ErrorNotification message={error} />}
        <div className="flex w-full flex-col">
          <Formik
            initialValues={{
              email: userEmail,
              password: "",
            }}
            validateOnChange={false}
            onSubmit={() => {
              router.push(
                "/signup/resend-activation?email=" +
                  encodeURIComponent(userEmail),
              )
            }}
          >
            <Form className="w-full">
              <Field
                name="email"
                field="input"
                label="Email address"
                type="email"
                validation={{
                  required: true,
                  email: true,
                }}
              />
              <Button
                variant="primary"
                width="fill"
                type="submit"
                className="mb-5"
                status="idle"
                leftIcon="icon-unread"
              >
                Resend activation link
              </Button>
            </Form>
          </Formik>
        </div>
      </>
    )
  }

  return (
    <>
      {error && <ErrorNotification message={error} />}
      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form className="w-full">
            <Field
              name="email"
              field="input"
              label="Email address"
              type="email"
              validation={{
                required: true,
                email: true,
              }}
            />
            <Field
              name="password"
              field="input"
              type="password"
              label="Password"
              className="mb-8"
              validation={{
                required: true,
              }}
              suffix={
                <Link href="/password-reset" passHref legacyBehavior>
                  <UiLink size="small">Need help?</UiLink>
                </Link>
              }
            />
            <Button
              variant="primary"
              width="fill"
              type="submit"
              className="mb-5"
              status={isSubmitting ? "loading" : "idle"}
            >
              Login
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )

  async function handleSubmit(values: FormValues) {
    const login = (await signIn("credentials", {
      ...values,
      redirect: false,
    })) as unknown as SignInResponse
    if (login.error) {
      const errorTitle = parseError(login.error)
      setError(errorTitle)
      setErrorCode(login.error)
      setUserEmail(values.email)
      if (process.env.NODE_ENV === "development") {
        console.error(login.error)
      }
    } else {
      setError(undefined)
      setErrorCode(undefined)
      await router.prefetch("/app")
      await router.push("/app")
    }
  }
}
